// The conversation drawn as a macOS inspector (APPLE-SPEC §6.3): plain 13 / 19 text blocks for the agent (no avatar), the person's words
// in a `--fill` bubble at the end of the line (with a scope sub-line when the change was for the department), centred day and "now on …"
// dividers, and the composer: a growing field with attach at its end, the model route line, the host's segmented controls and a 26 px
// send button, then the "Drafts and options only" note.
// Anchoring: a short thread reads from the top, directly under the context line, and the page's suggestions follow it as a hairline group;
// the free space collects above the composer. A long thread is scrolled to its latest turn, "Show n earlier messages" sits on its own 28 px
// row directly above it (the thread's top edge fades under the row), and the suggestions become one row of quiet chips above the composer.
// The feed follows new content while it streams. Classes `.ea-*` are styled by apps/assistant/src/econic/sidebar/sidebar.css. WP-SIDEBAR.
import { useCallback, useEffect, useLayoutEffect, useRef, useState, type KeyboardEvent, type ReactNode, type RefObject } from "react";
import { ArrowUp, ChevronRight, Paperclip, Sparkles, Square } from "lucide-react";
import { useTranslation } from "@ncp/i18n";
import { useAgentFace } from "./faceSlot";
import {
  Conversation,
  ConversationContent,
  ConversationScrollButton,
  MarkdownBody,
  PromptInput,
  PromptInputAttachment,
  PromptInputTextarea,
  Sources,
  StreamingCaret,
  citationMarkers,
  useConversation,
  type ChatMessageLike,
  type CitationLike,
} from "@ncp/ui";

/** A transcript entry of the inspector: a chat message, or a divider ("Today · 08:22", "Now on Early Warning · Signals"). */
export type InspectorEntry =
  | (ChatMessageLike & { kind?: "message"; scope?: string | null; /** Citation numbers of Restricted sources (amber tags). */ restricted?: number[] })
  | { kind: "divider"; id: string; label: ReactNode; tone?: "context" | "handoff" | "day" };

export interface InspectorChatProps {
  entries: InspectorEntry[];
  onSend: (text: string) => void;
  onStop?: () => void;
  busy?: boolean;
  draft: string;
  onDraftChange: (text: string) => void;
  onComposerKeyDown?: (e: KeyboardEvent<HTMLTextAreaElement>) => boolean | void;
  inputRef?: RefObject<HTMLTextAreaElement | null>;
  onAttach?: (files: File[]) => void;
  accept?: string;
  placeholder: string;
  onOpenCitation?: (c: CitationLike) => void;
  assistantName: string;
  thinking: string;
  emptyState?: ReactNode;
  /** Inside the field, above the text: files waiting to go, the mode chip. */
  composerTop?: ReactNode;
  /** Between the field and the controls: the model route line. */
  routeLine?: ReactNode;
  /** The bar under the field, before send: segmented controls. */
  controls?: ReactNode;
  /** Above the composer: a completion menu. */
  footer?: ReactNode;
  /** The page's suggestions: a hairline group after a short thread, one row of quiet chips above the composer after a long one. */
  chips?: Array<{ text: string; onPick: () => void }>;
  note?: ReactNode;
  labels: { send: string; stop: string; attach: string; earlier: (n: number) => string; suggested: string; latest?: string };
  /** How many entries are drawn before older ones fold away (they count in "Show n earlier messages"). */
  window?: number;
}

/** Heights the fit reckons with (sidebar.css): a suggestion row, the group's head and margin, the chips row, the thread's 12 px gap. */
const ROW = 32, GROUP_HEAD = 28, CHIPS_ROW = 32, GAP = 12, EARLIER_ROW = 28;

export function InspectorChat({ entries, onSend, onStop, busy, draft, onDraftChange, onComposerKeyDown, inputRef, onAttach, accept, placeholder, onOpenCitation, assistantName, thinking, emptyState, composerTop, routeLine, controls, footer, chips, note, labels, window = 14 }: InspectorChatProps) {
  const fileRef = useRef<HTMLInputElement>(null);
  const zone = useRef<HTMLDivElement>(null);
  const chat = useRef<HTMLDivElement>(null);
  const spacer = useRef<HTMLDivElement>(null);
  const localRef = useRef<HTMLTextAreaElement>(null);
  const textareaRef = inputRef ?? localRef;
  const [shown, setShown] = useState(window);
  useEffect(() => setShown(window), [window]);

  // Files dropped over the agent column anywhere (the shell's GlobalDrop) arrive as `econic:attach` on this element.
  useEffect(() => {
    const el = zone.current;
    if (!el || !onAttach) return;
    const on = (e: Event) => {
      const files = (e as CustomEvent<{ files?: File[] }>).detail?.files;
      if (files?.length) onAttach(files);
    };
    el.addEventListener("econic:attach", on);
    return () => el.removeEventListener("econic:attach", on);
  }, [onAttach]);

  // The field grows with its text; when the column's width changes (opening, resizing, the overlay sliding in) it is measured again.
  useEffect(() => {
    const el = zone.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    let width = el.clientWidth;
    const observer = new ResizeObserver(() => {
      if (el.clientWidth === width) return;
      width = el.clientWidth;
      const input = textareaRef.current;
      if (!input) return;
      input.style.height = "auto";
      input.style.height = `${Math.min(input.scrollHeight, 160)}px`;
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [textareaRef]);

  const submit = () => {
    const text = draft.trim();
    if (!text || busy) return;
    onSend(text);
  };

  // Fold older entries, never in the middle of a turn: start the window at a user message.
  let start = Math.max(0, entries.length - shown);
  while (start > 0 && !isUser(entries[start])) start -= 1;
  const hidden = entries.slice(0, start).filter((e) => e.kind !== "divider").length;
  const visible = entries.slice(start);
  const last = entries[entries.length - 1];
  const lastStreaming = last && last.kind !== "divider" && (last as ChatMessageLike).streaming;
  const suggestions = !busy && chips?.length ? chips.slice(0, 3) : [];

  // ---- fit: where the suggestions go and whether the thread is long (APPLE-SPEC §6.3; the prototype's FIT)
  const [fit, setFit] = useState<{ placement: "inline" | "chips"; long: boolean }>({ placement: "inline", long: false });
  const [above, setAbove] = useState(0);
  const scrollOf = () => chat.current?.querySelector<HTMLDivElement>(".ea-scroll") ?? null;
  const measure = useCallback(() => {
    const scroll = scrollOf();
    const content = scroll?.firstElementChild as HTMLElement | null;
    if (!scroll || !content) return;
    const group = content.querySelector<HTMLElement>(":scope > .ea-suggest");
    const row = chat.current?.querySelector<HTMLElement>(":scope > .ea-earlier-row");
    const strip = chat.current?.querySelector<HTMLElement>(":scope > .ea-chips");
    const threadH = content.offsetHeight - (group ? group.offsetHeight + GAP : 0);
    const avail = scroll.clientHeight + (spacer.current?.offsetHeight ?? 0) + (row?.offsetHeight ?? 0) + (strip?.offsetHeight ?? 0);
    const n = suggestions.length;
    const groupH = n ? GROUP_HEAD + ROW * n + GAP + 2 : 0;
    const next = n && threadH + groupH <= avail ? { placement: "inline" as const, long: hidden > 0 }
      : threadH <= avail - (n ? CHIPS_ROW : 0) ? { placement: "chips" as const, long: hidden > 0 }
      : { placement: "chips" as const, long: true };
    setFit((f) => (f.placement === next.placement && f.long === next.long ? f : next));
  }, [suggestions.length, hidden]);
  useLayoutEffect(measure);
  useEffect(() => {
    const el = chat.current;
    const scroll = scrollOf();
    if (!el || !scroll || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(() => measure());
    observer.observe(el);
    if (scroll.firstElementChild) observer.observe(scroll.firstElementChild);
    return () => observer.disconnect();
  }, [measure]);

  // ---- long thread: count the turns above the top edge (plus the folded ones) for "Show n earlier messages"
  const count = useCallback(() => {
    const scroll = scrollOf();
    const content = scroll?.firstElementChild as HTMLElement | null;
    if (!scroll || !content) return;
    const top = scroll.getBoundingClientRect().top + 14;
    let n = 0;
    for (const c of Array.from(content.children)) if (c.hasAttribute("data-message") && c.getBoundingClientRect().top < top) n += 1;
    setAbove(n);
  }, []);
  useLayoutEffect(() => { if (fit.long) count(); });
  useEffect(() => {
    const scroll = scrollOf();
    if (!scroll || !fit.long) return;
    let frame = 0;
    const on = () => { cancelAnimationFrame(frame); frame = requestAnimationFrame(count); };
    scroll.addEventListener("scroll", on, { passive: true });
    return () => { scroll.removeEventListener("scroll", on); cancelAnimationFrame(frame); };
  }, [fit.long, count]);
  const earlier = fit.long ? hidden + above : 0;
  // When the row appears while the reader scrolls, the thread gives it its height without the text moving under the reader's eyes.
  const rowWas = useRef(false);
  useLayoutEffect(() => {
    const on = earlier > 0;
    const scroll = scrollOf();
    if (on && !rowWas.current && scroll) scroll.scrollTop += EARLIER_ROW;
    rowWas.current = on;
  }, [earlier]);
  const showEarlier = () => {
    if (hidden > 0) setShown(entries.length);
    requestAnimationFrame(() => requestAnimationFrame(() => scrollOf()?.scrollTo({ top: 0, behavior: "smooth" })));
  };

  return (
    <div className="ea-chat" ref={chat}>
      {earlier > 0 ? (
        <div className="ea-earlier-row">
          <button type="button" className="ea-earlier" onClick={showEarlier}>{labels.earlier(earlier)}</button>
        </div>
      ) : null}
      <Conversation className="ea-feed">
        <ConversationContent className="ea-thread" scrollClassName={earlier > 0 ? "ea-scroll long" : "ea-scroll"} role="log" aria-relevant="additions" aria-label={assistantName}>
          {entries.length === 0 && emptyState ? emptyState : null}
          {visible.map((e) => (e.kind === "divider"
            ? <div key={e.id} className="ea-divider" data-tone={e.tone ?? "context"} role="separator">{e.label}</div>
            : <Entry key={e.key ?? e.id} m={e} assistantName={assistantName} onOpenCitation={onOpenCitation} />))}
          {busy && !lastStreaming ? <ThinkingLine text={thinking} /> : null}
          {fit.placement === "inline" && suggestions.length ? <SuggestionsGroup items={suggestions} label={labels.suggested} /> : null}
        </ConversationContent>
        <ConversationScrollButton className="ea-jump" label={labels.latest} />
        <FollowOnSend busy={!!busy} count={entries.length} />
      </Conversation>
      <div className="ea-grow" ref={spacer} aria-hidden />
      {fit.placement === "chips" && suggestions.length ? (
        <div className="ea-chips" role="group" aria-label={labels.suggested}>
          {suggestions.map((c) => <button key={c.text} type="button" className="ea-chip" dir="auto" onClick={c.onPick} title={c.text}>{c.text}</button>)}
        </div>
      ) : null}
      {footer ? <div className="ea-footer">{footer}</div> : null}
      <div ref={zone} className="ea-composer-zone" data-attach-files="" data-busy={busy ? "" : undefined}>
        <PromptInput onSubmit={submit} onFiles={onAttach && !busy ? onAttach : undefined} className="ea-composer">
          {composerTop}
          <div className="ea-in">
            <PromptInputTextarea
              ref={textareaRef}
              value={draft}
              onChange={(e) => onDraftChange(e.target.value)}
              onKeyDown={(e) => { if (onComposerKeyDown?.(e)) e.preventDefault(); }}
              onSubmitKey={submit}
              placeholder={placeholder}
              aria-label={placeholder}
              maxHeight={160}
              className="ea-input"
            />
            {onAttach ? (
              <>
                <input ref={fileRef} type="file" multiple accept={accept} className="hidden" onChange={(e) => { const files = Array.from(e.target.files ?? []); if (files.length) onAttach(files); e.target.value = ""; }} />
                <button type="button" className="ea-icon attach" title={labels.attach} aria-label={labels.attach} disabled={busy} onClick={() => fileRef.current?.click()}><Paperclip aria-hidden /></button>
              </>
            ) : null}
          </div>
          {routeLine ? <div className="ea-route">{routeLine}</div> : null}
          <div className="ea-bar">
            {controls}
            <span className="grow" />
            {busy && onStop ? (
              <button type="button" className="ea-send" onClick={onStop} title={labels.stop} aria-label={labels.stop}><Square aria-hidden className="fill-current" /></button>
            ) : (
              <button type="submit" className="ea-send" disabled={busy || !draft.trim()} title={labels.send} aria-label={labels.send}><ArrowUp aria-hidden /></button>
            )}
          </div>
        </PromptInput>
      </div>
      {note ? <p className="ea-note">{note}</p> : null}
    </div>
  );
}

/** "Suggested for this page": a white hairline group of 32 px prompts with a teal sparkle and a chevron (APPLE-SPEC §7.1). */
function SuggestionsGroup({ items, label }: { items: Array<{ text: string; onPick: () => void }>; label: string }) {
  return (
    <div className="ea-suggest" role="group" aria-label={label}>
      <span className="hd">{label}</span>
      {items.map((s) => (
        <button key={s.text} type="button" className="row" onClick={s.onPick} title={s.text}>
          <Sparkles aria-hidden className="ic" />
          <span className="tx" dir="auto">{s.text}</span>
          <ChevronRight aria-hidden className="chv" />
        </button>
      ))}
    </div>
  );
}

/** A message going out (typed, a starter, an answer card, a host's request) brings the feed back to its end, wherever the reader was. */
function FollowOnSend({ busy, count }: { busy: boolean; count: number }) {
  const ctx = useConversation();
  const was = useRef(busy);
  const seen = useRef(count);
  useEffect(() => {
    const started = busy && !was.current;
    const grew = count > seen.current && busy;
    was.current = busy;
    seen.current = count;
    if (started || grew) ctx?.scrollToBottom("smooth");
  }, [busy, count, ctx]);
  return null;
}

const isUser = (e: InspectorEntry | undefined) => !!e && e.kind !== "divider" && (e as ChatMessageLike).role === "user";
const MARKER = /\[\s*S\d/;

/** "Thinking…" before the answer's first frame: the host's face thinking (faceSlot.tsx), else the pulsing dot. */
function ThinkingLine({ text }: { text: string }) {
  const face = useAgentFace({ agentKey: null, state: "thinking", size: 20 });
  return <p className="ea-thinking" aria-live="polite">{face ?? <span className="dot" aria-hidden />}{text}</p>;
}

function Entry({ m, assistantName, onOpenCitation }: { m: ChatMessageLike & { scope?: string | null; restricted?: number[] }; assistantName: string; onOpenCitation?: (c: CitationLike) => void }) {
  const { t } = useTranslation();
  const body = useRef<HTMLDivElement>(null);
  // Restricted sources read as amber citation tags (APPLE-SPEC §6.3); the markers are drawn by the chat kit, so they are marked here.
  const restricted = m.restricted?.join(",") ?? "";
  useLayoutEffect(() => {
    const el = body.current;
    if (!el) return;
    const set = new Set(restricted ? restricted.split(",") : []);
    el.querySelectorAll<HTMLButtonElement>(".ea-md button.inline-grid").forEach((b) => b.toggleAttribute("data-restricted", set.has((b.textContent ?? "").trim())));
  });
  if (m.role === "user") {
    return (
      <div className="ea-msg-u" data-message="user">
        {m.content ? <p dir="auto">{citationMarkers(m.content)}</p> : null}
        {m.attachments?.length ? (
          <div className="files">{m.attachments.map((a) => <PromptInputAttachment key={a.name} name={a.name} status={a.status} />)}</div>
        ) : null}
        {m.scope ? <span className="sc">{m.scope}</span> : null}
      </div>
    );
  }
  if (m.bare) return <div className="ea-msg-bare" data-message="system">{m.before}{m.after}</div>;
  // Answers that mark their sources inline need no folded list under them; the tags open the same source.
  const inline = !!m.content && MARKER.test(m.content);
  return (
    <div ref={body} className="ea-msg-a" data-message="assistant" data-streaming={m.streaming ? "" : undefined} aria-label={m.author ?? assistantName}>
      {m.author ? <p className="by">{m.author}</p> : null}
      {m.before}
      {m.content
        ? <MarkdownBody streaming={m.streaming} citations={m.citations} onOpenCitation={onOpenCitation} renderWidget={m.renderWidget} className="ea-md">{m.content}</MarkdownBody>
        : m.streaming && !m.before ? <StreamingCaret active /> : null}
      {m.citations && m.citations.length > 0 && !inline ? <Sources citations={m.citations} onOpen={onOpenCitation} className="ea-sources" label={t("assistant:econic.sidebar.sources", { count: m.citations.length, defaultValue: m.citations.length === 1 ? "1 source" : "{{count}} sources" })} /> : null}
      {m.after}
    </div>
  );
}
